import { useCallback, useEffect } from 'react'
// import { useStoreState } from '../../../../../store/hooks'
import useScript from '../../../hooks/useScript'
import { LoginState, UserData } from '../../GlobalHeader'
import { getAppConfig } from '../AppConfigService'
import {
    AnalyticsEventMap,
    BotChallengeEvent,
    CTASelectedEvent,
    ClickVideoEvent,
    CloseGuestWelcomeEvent,
    CompleteLoginEvent,
    CompleteSignupEvent,
    CompletedPuzzleEvent,
    ConfirmEmailEvent,
    CreateGuestAccountEvent,
    CustomChallengeEvent,
    DailyBonusClickedEvent,
    DailyBonusCollectedEvent,
    DirectChallengeEvent,
    EmailActivatedEvent,
    EnterAutoPoolEvent,
    ExitAutoPoolEvent,
    FeedbackPopupClosedEvent,
    FeedbackPopupShownEvent,
    FeedbackSharedEvent,
    PvpEmoteMenuOpenedEvent,
    PvpEmoteSentEvent,
    FlipBoardPressedEvent,
    FtueEndDialogueEvent,
    FtuePuzzleCompletedEvent,
    FtuePuzzleStartedEvent,
    GameButtonPressedEvent,
    GameFinishedEvent,
    GameOfferResultEvent,
    GameOfferSentEvent,
    GameStartedEvent,
    GuestContinuedEvent,
    LoginDismissedEvent,
    LoginPromptShownEvent,
    LogoutEvent,
    MenuButtonClickedEvent,
    MissionClaimedEvent,
    MissionCompletedEvent,
    NextPuzzleEvent,
    OfferRematchEvent,
    OfferRematchResultEvent,
    OpenLoginEvent,
    OpenSignupEvent,
    PopupClosedEvent,
    PopupEvents,
    PopupShownEvent,
    PowerUpUsedEvent,
    RequestResendEvent,
    ResignEvent,
    RewardEvent,
    ShareClickedEvent,
    ShareLinkCopiedEvent,
    SignupReachSecondPageEvent,
    StartedPuzzleEvent,
    analyticsManager,
} from './AnalyticsManager'
import { getHighestUserGroupName } from './utils'

declare global {
    interface Window {
        dataLayer: Array<any>
    }
}

type Props = {
    env: string
    gtmId: string
    userData?: UserData
    loginState: LoginState
    loaded: () => void
}

export const GoogleAnalyticsServiceComponent = ({ env, userData, gtmId, loginState, loaded }: Props) => {
    // Helper function to get user ratings with fallbacks
    const getUserRatings = () => {
        if (!userData?.ratings)
            return { bulletRating: 0, blitzRating: 0, rapidRating: 0, classicRating: 0, userPuzzleRating: 0 }

        return {
            bulletRating: userData.ratings.bullet?.rating || 0,
            blitzRating: userData.ratings.blitz?.rating || 0,
            rapidRating: userData.ratings.rapid?.rating || 0,
            classicRating: userData.ratings.classic?.rating || 0,
            userPuzzleRating: (userData as any)?.puzzleRating || (userData as any)?.ratings?.puzzle?.rating || 0,
        }
    }

    // Helper function to get complete user properties for GTM
    const getUserProperties = () => {
        const ratings = getUserRatings()
        return {
            title: userData?.title || '',
            country: userData?.country || '',
            ...ratings,
        }
    }

    const sendCustomEvent = (eventData: any) => {
        const userId = eventData.userId || userData?.id || ''
        const userName = eventData.userName || userData?.user_name || ''
        const userType = userId ? getHighestUserGroupName(userData?.groups || []) : 'visitor'
        const platform = getAppConfig()?.isMobileApp ? 'mobile' : 'web'

        const data = {
            ...eventData,
            userId,
            userName,
            userType,
            user_id: userId, // GTM expects both userId and user_id
            timestamp: new Date().toISOString(),
            platform,
        }
        window.dataLayer.push(data)
        env !== 'prod' && console.log(`[GoogleAnalyticsService] ${eventData.event || ''} event data:`, data)
    }

    useEffect(() => {
        const eventHandlers: { [key in keyof AnalyticsEventMap]?: Function } = {
            botChallenge: (e: BotChallengeEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'botChallenge',
                    botID: e.botId, // GTM expects botID instead of botId
                })
            },
            completeLogin: (e: CompleteLoginEvent) => {
                sendCustomEvent({ ...e, event: 'completeLogin' })
            },
            completeSignup: (e: CompleteSignupEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'completeSignup',
                    countrySignup: e.country,
                    isOver12: userData?.over_12_years_old ?? true, // Use actual user data if available
                })
            },
            openSignup: (e: OpenSignupEvent) => {
                sendCustomEvent({ ...e, event: 'openSignup' })
            },
            clickVideo: (e: ClickVideoEvent) => {
                sendCustomEvent({ ...e, event: 'clickVideo' })
            },
            logout: (e: LogoutEvent) => {
                sendCustomEvent({ event: 'logout' })
            },
            gameOfferSent: (e: GameOfferSentEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'gameOfferSent',
                })
            },
            gameOfferResult: (e: GameOfferResultEvent) => {
                sendCustomEvent({ ...e, event: 'gameOfferResult' })
            },
            openLogin: (e: OpenLoginEvent) => {
                sendCustomEvent({ ...e, event: 'openLogin' })
            },
            exitAutoPool: (e: ExitAutoPoolEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'exitAutoPool',
                    challengeString: `${e.minutes}+${e.increment}`,
                })
            },
            customChallenge: (e: CustomChallengeEvent) => {
                sendCustomEvent({ ...e, event: 'customChallenge' })
            },
            offerRematch: (e: OfferRematchEvent) => {
                sendCustomEvent({ ...e, event: 'offerRematch' })
            },
            resign: (e: ResignEvent) => {
                sendCustomEvent({ ...e, event: 'resign' })
            },
            completedPuzzle: (e: CompletedPuzzleEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'completedPuzzle',
                    datePlayed: new Date().toISOString(),
                    userRating: e.userPuzzleRating, // GTM expects userRating instead of userPuzzleRating
                })
            },
            gameFinished: (e: GameFinishedEvent) => {
                const userProps = getUserProperties()
                sendCustomEvent({
                    ...e,
                    event: 'gameFinished',
                    ...userProps,
                })
            },
            enterAutoPool: (e: EnterAutoPoolEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'enterAutoPool',
                    challengeString: `${e.minutes}+${e.increment}`,
                })
            },
            gameStarted: (e: GameStartedEvent) => {
                const userProps = getUserProperties()
                sendCustomEvent({
                    ...e,
                    event: 'gameStarted',
                    challengeString: `${e.minutes}+${e.increment}`,
                    matchSource: 'auto_pool', // Default value, should be passed from event
                    ...userProps,
                })
            },
            reward: (e: RewardEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'reward',
                    // Ensure proper date format for GTM
                    expirationDate: e.expirationDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                })
            },
            directChallenge: (e: DirectChallengeEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'directChallenge',
                    challengeID: `${e.userID}-${e.opponentID}-${Date.now()}`, // Generate challenge ID if not provided
                })
            },
            offerRematchResult: (e: OfferRematchResultEvent) => {
                sendCustomEvent({ ...e, event: 'offerRematchResult' })
            },
            emailActivated: (e: EmailActivatedEvent) => {
                sendCustomEvent({
                    ...e,
                    event: 'emailActivated',
                    user_Id: userData?.id || '', // GTM has typo: user_Id instead of user_id
                    countryActivated: userData?.country || '',
                })
            },
            loginPromptShown: (e: LoginPromptShownEvent) => {
                sendCustomEvent({ ...e, event: 'loginPromptShown' })
            },
            guestContinued: (e: GuestContinuedEvent) => {
                sendCustomEvent({ ...e, event: 'guestContinued' })
            },
            loginDismissed: (e: LoginDismissedEvent) => {
                sendCustomEvent({ ...e, event: 'loginDismissed' })
            },
            confirmEmail: (e: ConfirmEmailEvent) => {
                sendCustomEvent({ ...e, event: 'confirmEmail' })
            },
            requestResend: (e: RequestResendEvent) => {
                sendCustomEvent({ ...e, event: 'requestResend' })
            },
            closeGuestWelcome: (e: CloseGuestWelcomeEvent) => {
                sendCustomEvent({ ...e, event: 'closeGuestWelcome' })
            },
            createGuestAccount: (e: CreateGuestAccountEvent) => {
                sendCustomEvent({ ...e, event: 'createGuestAccount' })
            },
            signupReachSecondPage: (e: SignupReachSecondPageEvent) => {
                sendCustomEvent({ ...e, event: 'signupReachSecondPage' })
            },
            nextPuzzle: (e: NextPuzzleEvent) => {
                sendCustomEvent({ ...e, event: 'nextPuzzle' })
            },
            missionCompleted: (e: MissionCompletedEvent) => {
                sendCustomEvent({ ...e, event: 'missionCompleted' })
            },
            missionClaimed: (e: MissionClaimedEvent) => {
                sendCustomEvent({ ...e, event: 'missionClaimed' })
            },
            ftuePuzzleStarted: (e: FtuePuzzleStartedEvent) => {
                sendCustomEvent({ ...e, event: 'ftuePuzzleStarted' })
            },
            ftuePuzzleCompleted: (e: FtuePuzzleCompletedEvent) => {
                sendCustomEvent({ ...e, event: 'ftuePuzzleCompleted' })
            },
            popupTriggered: (e: PopupEvents) => {
                sendCustomEvent({ ...e, event: 'popupTriggered' })
            },
            popupShown: (e: PopupShownEvent) => {
                sendCustomEvent({ ...e, event: 'popupShown' })
            },
            popupClosed: (e: PopupClosedEvent) => {
                sendCustomEvent({ ...e, event: 'popupClosed' })
            },
            ctaSelected: (e: CTASelectedEvent) => {
                sendCustomEvent({ ...e, event: 'ctaSelected' })
            },
            dailyBonusClicked: (e: DailyBonusClickedEvent) => {
                sendCustomEvent({ ...e, event: 'dailyBonusClicked' })
            },
            dailyBonusCollected: (e: DailyBonusCollectedEvent) => {
                sendCustomEvent({ ...e, event: 'dailyBonusCollected' })
            },
            powerUpUsed: (e: PowerUpUsedEvent) => {
                sendCustomEvent({ ...e, event: 'powerUpUsed' })
            },
            ftueEndDialogue: (e: FtueEndDialogueEvent) => {
                sendCustomEvent({ ...e, event: 'ftueEndDialogue' })
            },
            feedbackPopupShown: (e: FeedbackPopupShownEvent) => {
                sendCustomEvent({ ...e, event: 'feedbackPopupShown' })
            },
            feedbackPopupClosed: (e: FeedbackPopupClosedEvent) => {
                sendCustomEvent({ ...e, event: 'feedbackPopupClosed' })
            },
            menuButtonClicked: (e: MenuButtonClickedEvent) => {
                sendCustomEvent({ ...e, event: 'menuButtonClicked' })
            },
            startedPuzzle: (e: StartedPuzzleEvent) => {
                sendCustomEvent({ ...e, event: 'startedPuzzle' })
            },
            gameButtonPressed: (e: GameButtonPressedEvent) => {
                sendCustomEvent({ ...e, event: 'gameButtonPressed' })
            },
            flipBoardPressed: (e: FlipBoardPressedEvent) => {
                sendCustomEvent({ ...e, event: 'flipBoardPressed' })
            },
            shareClicked: (e: ShareClickedEvent) => {
                sendCustomEvent({ ...e, event: 'shareClicked' })
            },
            shareLinkCopied: (e: ShareLinkCopiedEvent) => {
                sendCustomEvent({ ...e, event: 'shareLinkCopied' })
            },
            feedbackShared: (e: FeedbackSharedEvent) => {
                sendCustomEvent({ ...e, event: 'feedbackShared' })
            },
            pvpEmoteSent: (e: PvpEmoteSentEvent) => {
                sendCustomEvent({ ...e, event: 'pvpEmoteSent' })
            },
            pvpEmoteMenuOpened: (e: PvpEmoteMenuOpenedEvent) => {
                sendCustomEvent({ ...e, event: 'pvpEmoteMenuOpened' })
            },
        }

        console.log('[GoogleAnalyticsService] initialize google analytics service')

        // Add listeners using the stored references
        Object.entries(eventHandlers).forEach(([event, handler]) => {
            analyticsManager.addEventListener(event as any, handler as any)
        })

        return () => {
            // Remove listeners using the stored references
            Object.entries(eventHandlers).forEach(([event, handler]) => {
                analyticsManager.removeEventListener(event as any, handler as any)
            })
        }
    }, [userData?.id, userData?.groups])

    useEffect(() => {
        const userId = userData?.id
        const userType = userId ? getHighestUserGroupName(userData.groups) : 'visitor'
        window.dataLayer.push({ userType, userId: userId || '' })
    }, [userData?.id, userData?.groups])

    useEffect(() => {
        window.dataLayer.push({ env })
    }, [env])

    const callback = useCallback((err?: string | Event) => {
        // Open the analytics gate even when gtm.js is blocked: the other channels must keep working.
        try {
            if (err) return console.error(err)
            window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        } finally {
            loaded()
        }
    }, [])

    useScript(
        'gtm',
        `https://www.googletagmanager.com/gtm.js?id=${gtmId}`,
        {
            async: true,
            shouldLoad: loginState !== LoginState.INITIALIZING,
        },
        callback,
    )

    return null
}
